import {
  buildProps,
  definePropType,
  isArray,
  isBoolean,
  isNumber,
  isObject,
  isString,
} from '@jdesign-vue/utils'
import { UPDATE_MODEL_EVENT } from '@jdesign-vue/constants'

import type { ExtractPropTypes, PropType, StyleValue } from 'vue'
import type Statistic from './statistic.vue'

type StatisticValueType = string | number | boolean

export const statisticEmits = {
  [UPDATE_MODEL_EVENT]: (val: StatisticValueType[] | StatisticValueType) =>
    isArray(val) ||
    isNumber(val) ||
    isString(val) ||
    isBoolean(val) ||
    isObject(val),
  change: (val: StatisticValueType[] | StatisticValueType) =>
    isArray(val) ||
    isNumber(val) ||
    isString(val) ||
    isBoolean(val) ||
    isObject(val),
  click: (evt: MouseEvent) => evt instanceof MouseEvent,
}

export const statisticProps = buildProps({
  modelValue: {
    type: [Array as PropType<Array<string | number>>, String, Number, Boolean],
    default: () => [],
  },
  label: {
    type: [String, Number, Boolean, Object],
  },
  disabled: {
    type: Boolean,
  },
  /**
   * @description Setting the decimal point
   */
  decimalSeparator: {
    type: String,
    default: '.',
  },
  /**
   * @description Sets the thousandth identifier
   */
  groupSeparator: {
    type: String,
    default: ',',
  },
  /**
   * @description numerical precision
   */
  precision: {
    type: Number,
    default: 0,
  },
  /**
   * @description Custom numerical presentation
   */
  formatter: Function,
  /**
   * @description Numerical content
   */
  value: {
    type: Number,
    default: 0,
  },
  /**
   * @description Sets the prefix of a number
   */
  prefix: String,
  /**
   * @description  Sets the suffix of a number
   */
  suffix: String,
  /**
   * @description Numeric titles
   */
  title: String,
  /**
   * @description Styles numeric values
   */
  valueStyle: {
    type: definePropType<StyleValue>([String, Object, Array]),
  },
  background: {
    type: Boolean,
  },
  border: {
    type: Boolean,
    default: true,
  },
  width: {
    type: [String, Number],
    // default: 275,
  },
  size: {
    type: String,
    values: ['', 'small'],
  },
  style: {
    type: definePropType<StyleValue>([String, Object, Array]),
    default: '',
  },
  bottomStyle: {
    type: definePropType<StyleValue>([String, Object, Array]),
    default: '',
  },
  trend: {
    type: definePropType<'up' | 'down'>(String),
  },
  trendPrefix: {
    type: String,
  },
  trendValue: {
    type: [String, Number],
  },
  trendSuffix: {
    type: String,
    default: '%',
  },
  trendValueStyle: {
    type: definePropType<StyleValue>([String, Object, Array]),
  },
  direction: {
    type: String,
    values: ['horizontal', 'vertical'],
    default: 'vertical',
  },
  type: {
    type: String,
    values: ['', 'radio', 'checkbox'],
    default: '',
  },
  animation: {
    type: Boolean,
    default: false,
  },
  animationDuration: {
    type: Number,
    default: 1500,
  },
} as const)
export type StatisticProps = ExtractPropTypes<typeof statisticProps>

export type StatisticInstance = InstanceType<typeof Statistic>
